import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import AddEditPatientDocument from 'components/clinical-data/AddEditPatientDocument';
import { getPatientDocumentFileUrl } from 'components/clinical-data/documentUtils';
import { prepareComments } from 'components/clinical-data/PatientDocumentComments';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import PatientMedicalDocumentPdfModal from 'components/pdf-modal/PatientMedicalDocumentPdfModal';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import {
  Button,
  Grid,
  Icon,
  Loader,
  Message,
  Modal,
  Popup,
  SemanticICONS,
  SemanticWIDTHS,
  Table
} from 'semantic-ui-react';
import {
  getCdrDocumentFiles,
  getPatientDocumentText,
  setPatientDocumentArchiveFlag
} from 'services/patientRecordServices';
import { getPatientHistory } from 'services/treatmentHistoryServices';
import { getUserDisplay } from 'services/userSettingsServices';
import styled from 'styled-components';
import { PatientDocumentOwnerEntity, PatientDocumentType, PatientReportStatus } from 'ts-types/api.enums';
import {
  CdrDocumentFileContent,
  InterventionBookingSearchResult,
  PatientCdrDocumentDto,
  PatientDocumentTypeDto,
  PatientDto,
  PatientHistoryDto,
  TreatmentDto,
  UserDisplayDto
} from 'ts-types/api.types';
import { fromIsoDateString, isoToDisplayDate } from 'utils/dateUtils';
import { errorUtils } from 'utils/errorUtils';
import { getText } from 'utils/textUtils';

const PatientHistoryDiv = styled.div`

  flex: 1 1 auto;
  overflow: auto;

  .history-item-container {
    margin-bottom: 1.35rem;

    .link.ui.button {
      background: none !important;
      color: #2185d0 !important;
      box-shadow: none;
      padding: 0.15rem 0.35rem;
      border: 1px solid transparent;
    }

    .link.ui.button:hover {
      color: #114e7e !important;
    }

    .link.ui.button:focus {
      border: 1px dotted #114e7e;
    }

    .history-item-body {
      margin-left: 1rem;
    }

    .title {
      padding-bottom: 0.35rem;
      border-bottom: 1px dotted #c5c5c5;

      .item-date {
        font-weight: bolder;
        margin-right: 1rem;
      }

      .item-icon {
        margin-right: 0.25rem;
      }

      .item-type.requires-action,
      .item-icon.requires-action {
        color: #957212;

        i.red.icon {
          margin-left: 0.5rem;
          color: #957212 !important;
        }
      }

      .actions {
        display: inline-block;
        margin-left: 0.5rem;
      }
    }

    .simple-props {
      margin-top: 0.35rem;
      display: grid;
      grid-template-columns: min-content auto;
      grid-column-gap: 1.3rem;
      grid-row-gap: 0.3rem;
    }

    .label {
      color: #444;
      font-weight: bolder;
    }

    .notes-text {
      font-style: italic;
    }

    .detailed-section {
      margin-top: 0.85rem;

      > .label {
        margin-right: 0.7rem;
      }

      .remark {
        margin-left: 0.5rem;
      }

      .remark-text {
        font-style: italic;
      }
    }

    .unfit-table.ui[class*="very compact"].table {
      border: none;
      margin-top: 0.15rem;
      margin-bottom: 0;
      max-width: 37rem;
    }

    .prescription-meds-table.ui[class*="very compact"].table {
      border: none;
      margin-top: 0.15rem;
      margin-bottom: 0;
    }

    .prescription-meds-table.ui[class*="very compact"].table th {
      border-top: none;
      border-left: none;
      border-right: none;
      background: none;
      padding-top: 0.2rem;
      padding-bottom: 0.2rem;
    }

    .documents-table.ui[class*="very compact"].table {
      border: none;
      margin-top: 0.15rem;
      margin-bottom: 0;
    }

    .documents-table.ui[class*="very compact"].table th {
      border-top: none;
      border-left: none;
      border-right: none;
      background: none;
      padding-top: 0.2rem;
      padding-bottom: 0.2rem;
    }

    .document-cert-container {
      display: flex;
      max-width: 90px;

      .document-cert-member {
        i.icon {
          cursor: pointer;
        }
      }
    }
  }

`;

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

const StyledButton = styled(Button)`
  padding: 2px !important;
  margin-bottom: 2px !important;
  background: transparent !important;
`;

interface DocumentActionProps extends MessageSourceProps {
  iconName: SemanticICONS;
  popupMessage: string;
  onClick: () => void;
}

const DocumentActionButtonComp: React.FC<DocumentActionProps> = (
    props: DocumentActionProps) => {

  return <StyledButton
      icon
      type="button"
      onClick={() => props.onClick()}
  >
    <Popup
        trigger={<Icon color="blue" name={props.iconName}/>}
        content={props.getMessage(props.popupMessage)}
        size="small"
        position="top center"
        style={PopupStyle}
        inverted
    />
  </StyledButton>;
};

const DocumentActionButton = withMessages(DocumentActionButtonComp);

interface HistoryItem {
  itemDate: moment.Moment;
  itemType: string;
}

interface PatientHistoryProps extends MessageSourceProps, AuthConsumerRenderProps {
  patientDocumentTypesMap: { [code: string]: PatientDocumentTypeDto };
  patient: PatientDto;
}

const PatientHistoryComp: React.FC<PatientHistoryProps> = (
    props) => {

  const {patient, getMessage} = props;

  const cancelTokenSource = useRef(axios.CancelToken.source());

  const [dataLoaded, setDataLoaded] = useState(false);

  const [patientHistory, setPatientHistory] =
      useState<Partial<PatientHistoryDto>>({});

  const [items, setItems] =
      useState<HistoryItem[]>([]);

  const [interventionBooking, setInterventionBooking] = useState<InterventionBookingSearchResult | undefined>();

  const [successfulMessage, setSuccessfulMessage] = useState<string | undefined>();

  const [patientMedicalDocumentTextResponse, setPatientMedicalDocumentTextResponse] =
      useState<string | undefined>();
  const [patientMedicalDocumentTextOpen, setPatientMedicalDocumentTextOpen] =
      useState(false);

  const [patientMedicalDocumentPdfResponse, setPatientMedicalDocumentPdfResponse] =
      useState<CdrDocumentFileContent[]>([]);
  const [patientMedicalDocumentPdfOpen, setPatientMedicalDocumentPdfOpen] =
      useState(false);

  const [showSetControlledButton, setShowSetControlledButton] =
      useState(false);

  const [currentUserDisplay, setCurrentUserDisplay] =
      useState<UserDisplayDto>({
        id: -1,
        firstName: "",
        name: ""
      });

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  let interventionBookingDocumentsMap: { [key: number]: PatientCdrDocumentDto[] } = {};
  if (patientHistory.documents) {
    const docs = patientHistory.documents.filter(doc =>
        doc.ownerEntityId && doc.ownerEntity === PatientDocumentOwnerEntity.INTERVENTION);
    interventionBookingDocumentsMap = _.groupBy(docs, "ownerEntityId");
  }

  const fetchTreatments = async () => {
    try {
      const patientHistory = await getPatientHistory(patient.id, cancelTokenSource.current);
      setPatientHistory(patientHistory);

      const treatmentItems: HistoryItem[] = patientHistory.treatments
          .filter(treatment => !treatment.treatmentData || !treatment.treatmentData.intervention)
          .map(treatment => ({
            itemDate: fromIsoDateString(treatment.treatmentDate)!,
            itemType: "Treatment",
            ...treatment
          }));

      const documents = patientHistory.documents
          .filter(doc => !doc.treatmentId)
          .map(doc => ({
            itemDate: fromIsoDateString(doc.documentDate)!,
            itemType: "Document",
            ...doc
          }));

      const bookings = patientHistory.interventionBookings
          .map(booking => ({
            itemDate: fromIsoDateString(booking.appointmentDate)!,
            itemType: "InterventionBooking",
            ...booking
          }));

      const items = _.orderBy(
          [
            ...treatmentItems,
            ...documents,
            ...bookings
          ],
          "itemDate", "desc");

      setItems(items);
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }
    }
    setDataLoaded(true);
  }

  const fetchCurrentUserDisplay = async () => {
    const userDisplay = await getUserDisplay(cancelTokenSource.current);
    setCurrentUserDisplay(userDisplay);
  }

  useEffect(() => {
    fetchTreatments();

    fetchCurrentUserDisplay();

    return () => {
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel();
      }
    }
  }, []);

  if (!dataLoaded) {
    return <Loader active={true} size="small"/>;
  }

  const getInterventionDescription = (interventionId: number): string => {
    const intervention = patientHistory.interventionMap![interventionId];
    const lang = props.currentUser!.userConfig.language;
    const descriptions: { [key: string]: string } = {
      "de": intervention.description,
      "en": intervention.descriptionEn,
      "fr": intervention.descriptionFr,
      "it": intervention.descriptionIt
    }
    return descriptions[lang] || descriptions["de"];
  }

  const isAddingPatientDocument = () => {
    return !!interventionBooking;
  }

  const openAddPatientDocumentModal = (interventionBooking: InterventionBookingSearchResult) => {
    setInterventionBooking(interventionBooking);
  }

  const hideAddPatientDocumentModal = () => {
    setInterventionBooking(undefined);
  }

  const onSavePatientDocument = (successfullyUploaded: boolean) => {
    if (successfullyUploaded) {
      setSuccessfulMessage(getMessage("patientRecord.patientDocument.successfullyUploadedMessage"))
      fetchTreatments();
    }
    hideAddPatientDocumentModal();
  }

  const renderAddPatientDocumentModal = (): React.ReactNode => {

    if (!isAddingPatientDocument()) {
      return <></>;
    }

    return (
        <Modal
            open={isAddingPatientDocument()}
            onClose={() => hideAddPatientDocumentModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            {getMessage("patientDocument.header")}
          </Modal.Header>
          <FormModalContentContainer>
            <AddEditPatientDocument
                onCancel={hideAddPatientDocumentModal}
                onSave={onSavePatientDocument}
                patientId={patient.id}
                patient={patient}
                ownerEntityId={interventionBooking!.bookingId}
                ownerEntity={PatientDocumentOwnerEntity.INTERVENTION}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

  const hidePatientDocumentPdfModal = () => {

    setPatientMedicalDocumentPdfResponse([]);
    setPatientMedicalDocumentPdfOpen(false);
    setShowSetControlledButton(false);

    fetchTreatments();
  };

  const openPdf = async (document: PatientCdrDocumentDto) => {

    const treatmentId = document.treatmentId;

    // TODO vg 2023-06-10: handle all document types
    // if (document.unfitId !== null) {
    //   this.openUnableToWorkPdf(document.unfitId, treatmentId)
    // } else if (document.prescriptionId !== null) {
    //   this.openPrescriptionPdf(document.prescriptionId, treatmentId)
    // } else {
    //   this.setPatientDocumentPdfModalOpen(document)
    // }
    setPatientDocumentPdfModalOpen(document);
  }

  const setPatientDocumentPdfModalOpen = (document: PatientCdrDocumentDto) => {

    const documentId = document.documentId;
    const patientId = document.patientId;

    getCdrDocumentFiles(patientId, documentId, cancelTokenSource.current)
        .then(response => {
          setPatientMedicalDocumentPdfResponse(response);
          setPatientMedicalDocumentPdfOpen(true);
        })
        .catch((e: any) => e.response && handleError(e.response.data))

    if (PatientDocumentType.PATIENT_SYNOPSIS_REPORT === document.type &&
        (PatientReportStatus.PREPARED === document.reportStatus || document.reportStatus === null)) {
      setShowSetControlledButton(true);
    }
  }

  const handleError = (error: any) => {

    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.unknownDocumentHistory,
      errorUtils.unknownPatientDocumentId,
      errorUtils.unknownPatient
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(
              getMessage(violation.details !== "" ? violation.details : `error.${violation.errorCode}`)
          )
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  }
  const renderTextDocumentIcon = (document: PatientCdrDocumentDto) => {
    return <DocumentActionButton
        iconName="file alternate outline"
        popupMessage="patientRecord.patientDocument.document"
        onClick={() => setPatientDocumentTextModalOpen(document)}
    />;
  }

  const renderPatientMedicalDocumentTextModal = (): React.ReactNode => {

    return (
        <Modal
            open={patientMedicalDocumentTextOpen}
            onClose={() => hidePatientDocumentTextModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            {getMessage("patientRecord.patientDocument.textDocument.modalHeader")}
          </Modal.Header>
          <FormModalContentContainer>
            {renderPatientDocumentTextModalContent()}
          </FormModalContentContainer>
        </Modal>
    )
  }

  const renderPatientDocumentTextModalContent = () => {
    return <FinalForm
        onSubmit={() => {
        }}
        initialValues={{textContent: patientMedicalDocumentTextResponse}}
        subscription={{pristine: true, submitting: true}}
        render={renderPatientDocumentTextModalForm}
    />;
  }

  const renderPatientDocumentTextModalForm = () => {
    return <FormContainer>
      <FormContent>
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <label htmlFor="textContent">
                {getMessage("patientRecord.patientDocument.uploadPatientDocument.comment")}
              </label>
              <Field
                  name="textContent"
                  component={TextArea}
                  style={{minHeight: "20vh", maxHeight: "70vh", height: "70vh"}}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
        <FormButtonsRow>
          <div style={{float: "right"}}>
            <Button compact type="button" onClick={hidePatientDocumentTextModal}>
              {getMessage("button.cancel")}
            </Button>
          </div>
        </FormButtonsRow>
      </FormContent>
    </FormContainer>;
  };

  const setPatientDocumentTextModalOpen = (document: PatientCdrDocumentDto) => {
    const documentId = document.documentId;
    const patientId = document.patientId;

    getPatientDocumentText(patientId, documentId, cancelTokenSource.current)
        .then(response => {
          setPatientMedicalDocumentTextResponse(response);
          setPatientMedicalDocumentTextOpen(true);
        });
    // TODO vg 2023-06-10: handle errors
    // .catch((e: any) => this.handleError(e.response.data))
  }

  const hidePatientDocumentTextModal = () => {
    setPatientMedicalDocumentTextResponse(undefined);
    setPatientMedicalDocumentTextOpen(false);
  };

  const handleArchiveDocument = (patientMedicalDocument: PatientCdrDocumentDto) => {
    const type = patientMedicalDocument.type;

    let documentId = patientMedicalDocument.documentId;
    let patientId = patientMedicalDocument.patientId;

    console.log(patientMedicalDocument, "TEST");

    const manualUpload = patientMedicalDocument.manualUpload;
    if (PatientDocumentType.UNABLE_TO_WORK === type && !manualUpload) {
      documentId = patientMedicalDocument.unfitId !== null
          ? patientMedicalDocument.unfitId
          : patientMedicalDocument.documentId;
    } else if (PatientDocumentType.PRESCRIPTION === type && !manualUpload) {
      documentId = patientMedicalDocument.prescriptionId !== null
          ? patientMedicalDocument.prescriptionId
          : patientMedicalDocument.documentId;
    }

    setPatientDocumentArchiveFlag(
        patientId, documentId, type, true, cancelTokenSource.current)
        .then(response => {
          setSuccessfulMessage(getMessage("patientRecord.patientDocument.successfullyArchivedMessage"))
          fetchTreatments();
        })
        .catch((e: any) => setErrorMessage(getMessage('error.general')));
  };

  const getDocumentTypeDescription = (documentType: string) => {
    return getText(props.patientDocumentTypesMap[documentType].description, props.language);
  }

  return <PatientHistoryDiv>
    {successfulMessage && (
        <Message success onDismiss={() => setSuccessfulMessage(undefined)}>
          {successfulMessage}
        </Message>
    )}
    {errorMessage && (
        <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
          {errorMessage}
        </StyledErrorMessage>
    )}
    {items.map((item, ix) => {

      if (item.itemType === "Treatment") {
        const treatment = item as any as TreatmentDto;
        const unfit = treatment.unfit;
        const prescriptions = treatment.prescriptions;

        return <div key={`treatment-${treatment.id}`} className="history-item-container">
          <div className="title">
            <span className="item-date">{isoToDisplayDate(treatment.treatmentDate)}</span>
            <Icon className="item-icon" name="clipboard outline"/>
            <span className="item-type">{getMessage("treatment")}</span>
          </div>

          <div className="history-item-body">
            <div className="simple-props">
              {
                  treatment.reason &&
                  <>
                    <div className="label">{getMessage("treatment.info.reason")}</div>
                    <div>{treatment.reason}</div>
                  </>
              }

              {
                  treatment.tariff &&
                  <>
                    <div className="label">{getMessage("tariff")}</div>
                    <div>{treatment.tariff}</div>
                  </>
              }

              {
                  treatment.diagnosis &&
                  <>
                    <div className="label">{getMessage("treatment.diagnosis")}</div>
                    <div>{treatment.diagnosis}</div>
                  </>
              }

              {
                  treatment.notes &&
                  <>
                    <div className="label">{getMessage("treatmentForm.notes")}</div>
                    <div className="notes-text">{treatment.notes}</div>
                  </>
              }
            </div>

            {
                unfit && unfit.length > 0 &&
                <div className="detailed-section unfit">
                  <span className="label">
                    <Icon name="chevron right" size="small"/>
                    {getMessage('unfit.titleHeader')}
                  </span>
                  <Table className="unfit-table" compact="very">
                    <Table.Body>
                      {
                        unfit.map((uf, ix) => {

                          const tableWidths: SemanticWIDTHS[] = ["3", "6"];
                          const fromDate = moment(uf.fromDate).format("DD.MM.YYYY");
                          const toDate = moment(uf.toDate).format("DD.MM.YYYY");

                          return <Table.Row key={ix}>
                            <Table.Cell width={tableWidths[0]}>
                              {`${uf.percent} %`}
                            </Table.Cell>
                            <Table.Cell width={tableWidths[1]}>
                              {fromDate} - {toDate}
                            </Table.Cell>
                          </Table.Row>;
                        })
                      }
                    </Table.Body>
                  </Table>

                  {
                      treatment.unfitRemark &&
                      <div className="remark">
                        <span className="label">{getMessage("treatmentForm.notes")}:&nbsp;</span>
                        <span className="remark-text">{treatment.unfitRemark}</span>
                      </div>
                  }
                </div>
            }

            {
                prescriptions && prescriptions.length > 0 &&
                prescriptions.map(prescription => {
                  return <div key={`prescription-${prescription.id}`} className="detailed-section prescription">
                    <Icon name="chevron right" size="small"/>
                    <span className="label">{getMessage("prescription.title")}</span>
                    <span>{isoToDisplayDate(prescription.prescriptionDate)}</span>

                    <Table className="prescription-meds-table" compact="very">
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>{getMessage('medicament')}</Table.HeaderCell>
                          <Table.HeaderCell>{getMessage('dosage')}</Table.HeaderCell>
                          <Table.HeaderCell>{getMessage('remarks')}</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {
                          prescription.medicaments.map((medicament, ix: number) => {

                            const tableWidths: SemanticWIDTHS[] = ["8", "2", "4", "1"];

                            const selectedMedicament =
                                patientHistory.medicamentMap![medicament.medicamentId];
                            const medicamentDesc = selectedMedicament
                                ? selectedMedicament.description
                                : getMessage("prescription.notFound");

                            return <React.Fragment key={ix}>
                              <Table.Row>
                                <Table.Cell width={tableWidths[0]}>
                                  {medicamentDesc}
                                </Table.Cell>
                                <Table.Cell width={tableWidths[1]}>
                                  {medicament.dosage}
                                </Table.Cell>
                                <Table.Cell width={tableWidths[2]}>{medicament.remarks}</Table.Cell>
                              </Table.Row>
                            </React.Fragment>;
                          })
                        }
                      </Table.Body>
                    </Table>

                    {
                        prescription.remark &&
                        <div className="remark">
                          <span className="label">{getMessage("treatmentForm.notes")}:&nbsp;</span>
                          <span className="remark-text">{prescription.remark}</span>
                        </div>
                    }
                  </div>;
                })
            }
          </div>

        </div>;
      } else if (item.itemType === "Document") {
        const document = item as any as PatientCdrDocumentDto;

        // don't show documents linked to an intervention,
        // they will be rendered as part of that intervention
        if (document.ownerEntity === PatientDocumentOwnerEntity.INTERVENTION) {
          return null;
        }

        return <div key={`document-${document.id}`} className="history-item-container">
          <div className="title">
            <span className="item-date">{isoToDisplayDate(document.documentDate)}</span>
            <Icon
                className={`item-icon ${document.requiresAction ? "requires-action" : 0}`}
                name="file alternate outline"
            />
            <span className={`item-type ${document.requiresAction ? "requires-action" : 0}`}>
              {getDocumentTypeDescription(document.type)}
              {
                  document.requiresAction &&
                  <Icon name="warning circle" color="red"/>
              }
            </span>
          </div>

          <div className="history-item-body">
            <div className="simple-props">
              {
                  document.name &&
                  <>
                    <div className="label">
                      {getMessage("patientRecord.patientDocument.uploadPatientDocument.documentName")}
                    </div>
                    <div>{document.name}</div>
                  </>
              }

              {
                  document.description &&
                  <>
                    <div className="label">
                      {getMessage("patientRecord.patientDocument.uploadPatientDocument.documentDescription")}
                    </div>
                    <div>{document.description}</div>
                  </>
              }

              {/*{*/}
              {/*    document.textContent &&*/}
              {/*    <>*/}
              {/*      <div className="label">*/}
              {/*        {getMessage("patientRecord.patientDocument.uploadPatientDocument.comment")}*/}
              {/*      </div>*/}
              {/*      <div className="notes-text">{document.textContent}</div>*/}
              {/*    </>*/}
              {/*}*/}
            </div>
          </div>
        </div>
      } else if (item.itemType === "InterventionBooking") {
        const booking = item as any as InterventionBookingSearchResult;

        const interventionId = booking.interventionId;
        const intervention = patientHistory.interventionMap![interventionId];
        const interventionDescription = getInterventionDescription(interventionId);
        const documents =
            _.orderBy(interventionBookingDocumentsMap[booking.bookingId], "documentDate", "desc");

        return <div key={`booking-${booking.bookingId}`} className="history-item-container">
          <div className="title">
            <span className="item-date">{isoToDisplayDate(booking.appointmentDate)}</span>

            {
                intervention.color &&
                <Icon
                    className="item-icon"
                    name="circle"
                    style={{color: `${intervention.color}`}}
                    size="small"
                    title={interventionDescription}
                />
            }
            <span className="item-type">{interventionDescription}</span>

            <div className="actions">
              <Button className="link"
                      compact
                      onClick={() => openAddPatientDocumentModal(booking)}
              >
                {getMessage("patientRecord.attachDocument")}
              </Button>
            </div>

          </div>

          <div className="history-item-body">
            <div className="simple-props">
              {
                  booking.bookingRemarks &&
                  <>
                    <div className="label">
                      {getMessage("treatmentForm.notes")}
                    </div>
                    <div className="notes-text">{booking.bookingRemarks}</div>
                  </>
              }
            </div>
            {documents && (
                <div className="detailed-section">
                    <span className="label">
                      <Icon name="chevron right" size="small"/>
                      {getMessage('patientDocuments')}
                    </span>
                  <Table className="documents-table" compact="very">
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>{getMessage("patientRecord.patientDocument.documentDate")}</Table.HeaderCell>
                        <Table.HeaderCell>{getMessage("patientRecord.patientDocument.name")}</Table.HeaderCell>
                        <Table.HeaderCell>{getMessage("patientRecord.patientDocument.patientDescription")}</Table.HeaderCell>
                        <Table.HeaderCell>{getMessage("patientRecord.patientDocument.actionButtons")}</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {
                        documents.map((document, ix: number) => {

                          const tableWidths: SemanticWIDTHS[] = ["4", "4", "4", "2"];

                          return <React.Fragment key={ix}>
                            <Table.Row>
                              <Table.Cell width={tableWidths[0]}>
                                {moment(document.documentDate).format("DD.MM.YYYY")}
                              </Table.Cell>
                              <Table.Cell width={tableWidths[1]}>
                                {document.name}
                              </Table.Cell>
                              <Table.Cell width={tableWidths[2]}>
                                {document.description}
                              </Table.Cell>
                              <Table.Cell width={tableWidths[3]}>
                                <div className="document-cert-container">
                                  {
                                      (document.documentFile || document.documentText) &&
                                      <div className="document-cert-member">
                                        {document.documentFile
                                            ? <DocumentActionButton
                                                iconName="print"
                                                popupMessage={getDocumentTypeDescription(document.type)}
                                                onClick={() => openPdf(document)}
                                            />
                                            : document.documentText ? renderTextDocumentIcon(document) : ""
                                        }
                                      </div>
                                  }

                                  <div className="document-cert-member">
                                    <DocumentActionButton
                                        iconName="archive"
                                        popupMessage={getMessage('patientRecord.patientDocument.archiveDocument')}
                                        onClick={() => handleArchiveDocument(document)}
                                    />
                                  </div>
                                </div>
                              </Table.Cell>
                            </Table.Row>
                          </React.Fragment>;
                        })
                      }
                    </Table.Body>
                  </Table>
                </div>
            )}
          </div>
        </div>;
      }

      return <></>;
    })}

    {renderAddPatientDocumentModal()}
    {
        patientMedicalDocumentPdfOpen &&
        <PatientMedicalDocumentPdfModal
            pdfResponses={patientMedicalDocumentPdfResponse}
            modalOpen={patientMedicalDocumentPdfOpen}
            onCloseModal={hidePatientDocumentPdfModal}
            getPdfUrl={getPatientDocumentFileUrl}
            textContent={patientMedicalDocumentPdfResponse[0].textContent
                ? patientMedicalDocumentPdfResponse[0].textContent
                : ""}
            comments={prepareComments(patientMedicalDocumentPdfResponse[0].comments || [])}
            currentUserDisplay={currentUserDisplay}
            showSetControlledButton={showSetControlledButton}
            successfullyChangedReportStatus={() => {
              // TODO vg 2023-06-10: handle success message
            }}
        />
    }

  </PatientHistoryDiv>;
}

export const PatientHistory = withMessages(withAuthContext(PatientHistoryComp));
